/**
 * Metadata DSL Constants
 *
 * Immutable constants enforcing FERP architectural principles.
 * Life-critical: These define the allowed values for metadata validation.
 */

import { PrimitiveWorkflowType } from '../../shared/ReactFlowCanvas/types';
import type { WorkflowTemplateMetadata } from '../types/MetadataTypes';

/**
 * THE 16 PRIMITIVE WORKFLOW TYPES
 *
 * CRITICAL: This is the complete set. No additions, no removals.
 * All workflows MUST compile to these types only.
 * Enforced at backend validation and frontend runtime.
 */
export const SIXTEEN_PRIMITIVE_TYPES: readonly PrimitiveWorkflowType[] = Object.freeze([
  'WorkflowDefinition',
  'ActorDescriptor',
  'TransitionCatalog',
  'Policy',
  'Context',
  'Schedule',
  'Resource',
  'Integration',
  'Instance',
  'Signal',
  'Task',
  'JobStatus',
  'ExecutionResult',
  'TimelineItem',
  'Escalation',
  'OptimizationRecommendation',
] as const);

/**
 * Validate primitive type
 */
export function isValidPrimitiveType(type: string): type is PrimitiveWorkflowType {
  return (SIXTEEN_PRIMITIVE_TYPES as readonly string[]).includes(type);
}

/**
 * Assert primitive type (throws if invalid)
 * Life-critical: Runtime validation
 */
export function assertValidPrimitiveType(type: string): asserts type is PrimitiveWorkflowType {
  if (!isValidPrimitiveType(type)) {
    throw new Error(
      `Invalid primitive type: "${type}". ` +
      `Must be one of 16 allowed types: ${SIXTEEN_PRIMITIVE_TYPES.join(', ')}`
    );
  }
}

/**
 * Supported business domains
 * Domain-agnostic: Add new domains via metadata, not code
 */
export const SUPPORTED_DOMAINS = Object.freeze([
  'healthcare',
  'finance',
  'legal',
  'hr',
  'general',
  'manufacturing',
  'retail',
  'government',
  'education',
  'nonprofit',
] as const);

export type SupportedDomain = typeof SUPPORTED_DOMAINS[number];

/**
 * Metadata DSL versions
 * Preserve: Maintain backward compatibility
 */
export const DSL_VERSIONS = Object.freeze({
  V1_0: '1.0',
  V2_0: '2.0',
  CURRENT: '2.0',
} as const);

/**
 * Audit levels for life-critical compliance
 */
export const AUDIT_LEVELS = Object.freeze({
  STANDARD: 'standard',
  ENHANCED: 'enhanced',
  CRITICAL: 'critical',
} as const);

export type AuditLevel = typeof AUDIT_LEVELS[keyof typeof AUDIT_LEVELS];

/**
 * Transformation types
 * All supported transformation functions
 */
export const TRANSFORMATION_TYPES = Object.freeze([
  'direct',
  'boolean_to_rule',
  'array_to_json',
  'object_merge',
  'template',
  'custom',
  'conditional',
  'aggregate',
  'date_format',
  'string_transform',
  'number_transform',
] as const);

export type TransformationType = typeof TRANSFORMATION_TYPES[number];

/**
 * UI component types (Mantine-based)
 * Complete list of supported components
 */
export const UI_COMPONENT_TYPES = Object.freeze([
  'TextInput',
  'Textarea',
  'NumberInput',
  'PasswordInput',
  'Select',
  'MultiSelect',
  'Checkbox',
  'Radio',
  'Switch',
  'DateInput',
  'DateTimePicker',
  'TimeInput',
  'FileInput',
  'JsonEditor',
  'CodeEditor',
  'Slider',
  'Rating',
  'ColorInput',
  'SegmentedControl',
  'Autocomplete',
  'TagsInput',
  'PinInput',
  'CustomComponent',
] as const);

export type UIComponentType = typeof UI_COMPONENT_TYPES[number];

/**
 * Validation error codes
 * Standardized error codes for consistency
 */
export const VALIDATION_ERROR_CODES = Object.freeze({
  // Schema validation
  INVALID_SCHEMA_VERSION: 'E001',
  MISSING_REQUIRED_FIELD: 'E002',
  INVALID_FIELD_TYPE: 'E003',
  
  // Primitive validation
  INVALID_PRIMITIVE_TYPE: 'E101',
  PRIMITIVE_NOT_IN_SIXTEEN: 'E102',
  UNMAPPED_UI_FIELD: 'E103',
  PRIMITIVE_ID_NOT_FOUND: 'E104',
  INVALID_PRIMITIVE_METADATA: 'E105',
  
  // Mapping validation
  INVALID_FIELD_PATH: 'E201',
  CIRCULAR_DEPENDENCY: 'E202',
  INVALID_TRANSFORMATION: 'E203',
  
  // Compliance
  MISSING_COMPLIANCE_FRAMEWORK: 'E301',
  INSUFFICIENT_AUDIT_LEVEL: 'E302',
  
  // Custom
  CUSTOM_VALIDATION_FAILED: 'E901',
} as const);

/**
 * Default metadata for new templates
 * Preserve: All required fields with sensible defaults
 */
export const DEFAULT_TEMPLATE_METADATA: Partial<WorkflowTemplateMetadata> = {
  schema_version: '2.0',
  compliance: {
    frameworks: [],
    audit_level: 'standard',
    requires_approval: false,
    retention_policy: '7_years',
    data_classification: 'internal',
  },
  usage_count: 0,
  version_history: [],
};

/**
 * Compliance framework definitions
 * Life-critical: Healthcare, financial regulations
 */
export const COMPLIANCE_FRAMEWORKS = Object.freeze({
  HIPAA: {
    name: 'HIPAA',
    full_name: 'Health Insurance Portability and Accountability Act',
    required_audit_level: 'critical',
    retention_years: 6,
    encryption_required: true,
  },
  HITECH: {
    name: 'HITECH',
    full_name: 'Health Information Technology for Economic and Clinical Health Act',
    required_audit_level: 'critical',
    retention_years: 6,
  },
  SOX: {
    name: 'SOX',
    full_name: 'Sarbanes-Oxley Act',
    required_audit_level: 'enhanced',
    retention_years: 7,
  },
  GDPR: {
    name: 'GDPR',
    full_name: 'General Data Protection Regulation',
    required_audit_level: 'enhanced',
    right_to_erasure: true,
  },
  FDA_21_CFR_PART_11: {
    name: 'FDA_21_CFR_Part_11',
    full_name: 'FDA 21 CFR Part 11 (Electronic Records)',
    required_audit_level: 'critical',
    retention_years: 25,
    immutable_audit_trail: true,
  },
  FDCPA: {
    name: 'FDCPA',
    full_name: 'Fair Debt Collection Practices Act',
    required_audit_level: 'enhanced',
  },
} as const);

/**
 * Field validation patterns
 * Reusable regex patterns for common fields
 */
export const VALIDATION_PATTERNS = Object.freeze({
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE_US: /^\+?1?\s*\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/,
  SSN: /^\d{3}-?\d{2}-?\d{4}$/,
  ZIP_US: /^\d{5}(-\d{4})?$/,
  MRN: /^[A-Z0-9]{6,12}$/,  // Medical Record Number
  NPI: /^\d{10}$/,  // National Provider Identifier
  ICD10: /^[A-Z]\d{2}(\.\d{1,4})?$/,  // ICD-10 code
  CPT: /^\d{5}$/,  // CPT code
  URL: /^https?:\/\/.+/,
} as const);

/**
 * JSONPath validation
 * Ensures field paths are valid
 */
export function isValidJSONPath(path: string): boolean {
  try {
    // Basic validation - actual JSONPath parser will validate fully
    return path.startsWith('$') || path.startsWith('metadata.');
  } catch {
    return false;
  }
}

/**
 * Export type guard for metadata
 */
export function isWorkflowTemplateMetadata(obj: any): obj is WorkflowTemplateMetadata {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.template_id === 'string' &&
    obj.schema_version === '2.0' &&
    typeof obj.business === 'object' &&
    typeof obj.ui === 'object' &&
    typeof obj.primitives === 'object' &&
    Array.isArray(obj.primitives.nodes) &&
    obj.primitives.nodes.every((n: any) => isValidPrimitiveType(n.type))
  );
}

